import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { THEME_KEYS, ThemeKey } from '@common/constants/themes';

export const templateColors = [
  {
    name: THEME_KEYS.RECOMMENDED,
    description: 'Crisp & professional', // Technical & efficient
    colors: ['rgb(131, 188, 201)', 'rgb(251, 248, 242)', 'rgb(178, 163, 133)', 'rgb(66, 60, 57)'],
  },

  {
    name: THEME_KEYS.CREAMY,
    description: 'Warm & inviting', // Classic & refined
    colors: ['rgb(110, 25, 25)', 'rgb(228, 197, 138)', 'rgb(240, 132, 117)', 'rgb(225, 217, 199)'],
  },
  {
    name: THEME_KEYS.CONTEMPORARY,
    description: 'Bold & modern', // Dynamic & impactful
    colors: ['rgb(255, 248, 241)', 'rgb(224, 190, 154)', 'rgb(91, 77, 67)', 'rgb(162, 103, 45)'],
  },
  {
    name: THEME_KEYS.MATURE,
    description: 'Serene & thoughtful', // Gentle & welcoming
    colors: ['rgb(99, 55, 97)', 'rgb(254, 230, 196)', 'rgb(48, 87, 78)', 'rgb(145, 171, 165)'],
  },
  {
    name: THEME_KEYS.NOSTALGIC,
    description: 'Confident & memorable', // Natural & grounding
    colors: ['rgb(194, 39, 75)', 'rgb(255, 240, 236)', 'rgb(180, 200, 220)', 'rgb(85, 107, 141)'],
  },
  {
    name: THEME_KEYS.TECHNICAL,
    description: 'Energetic & creative', // Fresh & calming
    colors: ['rgb(32, 97, 183)', 'rgb(189, 233, 251)', 'rgb(234, 224, 218)', 'rgb(208, 224, 205)'],
  },
];

interface ColorThemeState {
  selectedThemeColor: ThemeKey | null;
  isLoading: boolean;
  isProductsLoadingComplete: boolean;
}

const initialState: ColorThemeState = {
  selectedThemeColor: null,
  isLoading: false,
  isProductsLoadingComplete: false,
};

export const colorThemeStateAtom = atom<ColorThemeState>(initialState);

export const useColorThemeState = () => {
  return useAtom(colorThemeStateAtom);
};

export const useSelectedThemeColor = () => {
  return useAtomValue(colorThemeStateAtom).selectedThemeColor;
};

export const useSetSelectedThemeColor = () => {
  const setColorThemeState = useSetAtom(colorThemeStateAtom);
  return (themeColor: ThemeKey | null) => {
    setColorThemeState((prev) => ({
      ...prev,
      selectedThemeColor: themeColor,
    }));
  };
};

export const useColorThemeLoading = () => {
  return useAtomValue(colorThemeStateAtom).isLoading;
};

export const useSetColorThemeLoading = () => {
  const setColorThemeState = useSetAtom(colorThemeStateAtom);
  return (isLoading: boolean) => {
    setColorThemeState((prev) => ({
      ...prev,
      isLoading,
    }));
  };
};

export const useResetColorThemeState = () => {
  const setColorThemeState = useSetAtom(colorThemeStateAtom);
  return () => setColorThemeState(initialState);
};

export const useProductsLoadingComplete = () => {
  return useAtomValue(colorThemeStateAtom).isProductsLoadingComplete;
};

export const useSetProductsLoadingComplete = () => {
  const setColorThemeState = useSetAtom(colorThemeStateAtom);
  return (isComplete: boolean) => {
    setColorThemeState((prev) => ({
      ...prev,
      isProductsLoadingComplete: isComplete,
    }));
  };
};
